import axios from "axios";

import authedAxios from "@api/authedAxios";
import { tsRestClient } from "@api/tsRest";

import C from "@constants/constants";

import { CampaignType, EmailType } from "@interfaces/campaign.type";

import { ISendCampaignResponse } from "@queries/campaign.query";

import chunkArrayInKB from "@utilities/chunkArrayInKB";

export const getCampaigns = async (technologyId: string) => {
  try {
    const response = await authedAxios("v2").get(`campaigns/list-campaigns-by-technology/${technologyId}`);
    return { body: response.data as CampaignType[], status: response.status };
  } catch (error: any) {
    return { body: [], status: error.status };
  }
};

export const deleteCampaign = async (campaignId: string) => {
  await authedAxios("v2").delete(`campaigns/delete-campaign/${campaignId}`);
};

// export const getCampaign = async (campaignId: string) => {
//   const response = await authedAxios("v2").get(`campaigns/${campaignId}`);
//   return response.data as ICampaign;
// };

export const getCampaign = async (campaignId: string) => {
  const { body, status } = await tsRestClient.campaign.findOne({
    params: {
      id: campaignId,
    },
  });
  if (status === 403) {
    return { body: null, status };
  }

  if (status !== 200) {
    throw new Error("Failed to get campaign");
  }

  return { body, status };
};

export const createCampaign = async (contactIds: string[], technologyId: string) => {
  const response = await authedAxios("v2").post("campaigns/create", {
    contactIds,
    technologyId,
  });
  return response.data as CampaignType;
};

export const updateCampaign = async (campaign: Partial<CampaignType>) => {
  const response = await authedAxios("v2").put<CampaignType>(`campaigns/update/${campaign.id}`, campaign);
  return response.data;
};

export const applyStandardTemplate = async (campaign: Partial<CampaignType>) => {
  const response = await authedAxios("v2").post(`campaigns/apply-standard-template/${campaign.id}`);
  return response.data as CampaignType;
};

export const applyNewTemplate = async (campaign: Partial<CampaignType>) => {
  const response = await authedAxios("v2").post(`campaigns/apply-new-template/${campaign.id}`);
  return response.data as CampaignType;
};

export const applySavedTemplate = async (campaign: Partial<CampaignType>) => {
  const response = await authedAxios("v2").post(`campaigns/apply-saved-template/${campaign.id}`);

  return response.data as CampaignType;
};

export const generateEmails = async (campaign: Partial<CampaignType>) => {
  const response = await authedAxios("v2").post(`campaigns/generate-emails/${campaign.id}`);

  return response.data as CampaignType;
};

export const saveEmails = async (campaign: Partial<CampaignType>) => {
  const emails: EmailType[] | undefined = campaign.emails;
  if (!emails) return;

  // Update the email objects to use campaignEmailId instead of id
  const updatedEmails = emails.map((email) => ({
    ...email,
    campaignEmailId: email.id,
  }));

  const batches = chunkArrayInKB(updatedEmails, C.MAX_REQUEST_BODY_SIZE_KB);

  const filteredBatches = batches.filter((batch) => batch.length > 0);

  if (filteredBatches.length === 0) return;

  const requests = filteredBatches.map((batch) => {
    return authedAxios("v2").post(`campaigns/save-emails`, { emails: batch });
  });

  const response = await Promise.all(requests);

  return response.at(-1)?.data as CampaignType;
};

export const removeEmails = async (campaignId: string, emailId: number) => {
  const response = await authedAxios("v2").post(`campaigns/remove-email/${campaignId}`, {
    emailId,
  });
  return response.data as CampaignType;
};

export const sendCampaign = async (campaignId: string, scheduleDateTime: Date | null) => {
  const response = await authedAxios("v2").post(`campaigns/send-campaign/${campaignId}`, {
    ...(scheduleDateTime && { scheduleDateTime: scheduleDateTime.getTime() }),
  });

  return response.data as ISendCampaignResponse;
};

export const cancelScheduledCampaign = async (campaignId: string) => {
  const response = await authedAxios("v2").post(`campaigns/cancel-scheduled-campaign/${campaignId}`);

  return response.data as CampaignType;
};

export type CampaignAttachmentsType = { mimetype: string; originalName: string; size: number }[];

export interface ICampaignAttachmentInputResponse {
  campaign: CampaignType;
  urls: string[];
}

export const getPresignedUrls = async (campaignId: string, fileObjects: CampaignAttachmentsType) => {
  const response = await authedAxios("v2").post(
    `campaigns/save-attachments/get-presigned-urls/${campaignId}`,
    fileObjects,
  );

  return response.data as ICampaignAttachmentInputResponse;
};

export const handleAttachmentUpload = async (urls: string[], files: File[]) => {
  const requests = urls.map((url, index) => {
    return axios.put(url, files[index], {
      headers: {
        "Content-Type": files[0]?.type,
      },
    });
  });
  const responses = await Promise.all(requests);

  return responses.every((response) => response.status === 200);
};

export const handleDeleteAttachment = async (attachmentId: number, attachmentName: string) => {
  const response = await authedAxios("v2").post(`campaigns/delete-attachment/`, {
    attachmentId,
    attachmentName,
  });

  return response.data as CampaignType;
};

export const handleUpdateEmail = async (campaignId: string, emailId: number, email: Partial<EmailType>) => {
  const response = await authedAxios("v2").put(`campaigns/${campaignId}/email/${emailId}`, {
    email,
  });

  return response.data as EmailType;
};

export const sendTestEmail = async (campaignId: string, email: string) => {
  await authedAxios("v2").post(`campaigns/send-test-email/${campaignId}`, { testEmailAddress: email });
};

export const sendEmail = async (emailId: number) => {
  const response = await authedAxios("v2").post(`campaigns/send-email/`, { emailId });

  return response.data as EmailType;
};
